import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";

const SETTING_FIELDS = [
    { key: "company_name", label: "settings.companyName" },
    { key: "support_phone", label: "settings.supportPhone" },
    { key: "support_email", label: "settings.supportEmail" },
    { key: "items_per_page", label: "settings.itemsPerPage" },
    { key: "default_language", label: "settings.defaultLanguage" },
];

const Settings = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isAdmin = role === "admin";
    const isStaff = role === "admin" || role === "moderator";

    const [settings, setSettings] = useState({});
    const [dicts, setDicts] = useState({});
    const [updates, setUpdates] = useState(0);
    const [newItem, setNewItem] = useState({ type: "", value: "", label: "", sort_order: 0 });

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    useEffect(() => {
        axios.get("/api/settings").then((res) => setSettings(res.data.data || {})).catch(() => {});
    }, []);
    useEffect(() => {
        if (!isStaff) return;
        axios.get("/api/dictionaries").then((res) => setDicts(res.data.data || {})).catch(() => {});
    }, [isStaff, updates]);

    if (!isStaff) {
        return <div className="page-content-main-container"><h2 className="page-title">{t("settings.title")}</h2></div>;
    }

    const saveSettings = () => {
        axios.put("/api/settings", settings)
            .then((res) => notify(res.data.message.en, "success-notification"))
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const setDictField = (type, id, patch) => setDicts((d) => ({ ...d, [type]: d[type].map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
    const saveDict = (item) => {
        axios.put(`/api/dictionaries/${item.id}`, { label: item.label, sort_order: item.sort_order, enabled: item.enabled })
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };
    const deleteDict = (id) => {
        if (!window.confirm(t("settings.confirmDelete"))) return;
        axios.delete(`/api/dictionaries/${id}`)
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };
    const addItem = () => {
        if (!newItem.type.trim() || !newItem.value.trim()) return;
        axios.post("/api/dictionaries", newItem)
            .then((res) => { notify(res.data.message.en, "success-notification"); setNewItem({ type: "", value: "", label: "", sort_order: 0 }); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const types = Object.keys(dicts);

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("settings.title")}</h2>

            <h3 style={{ fontSize: "15px", margin: "6px 0 12px" }}>{t("settings.general")}</h3>
            <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", maxWidth: "520px", marginBottom: "26px" }}>
                {SETTING_FIELDS.map((f) => (
                    <div key={f.key} style={{ marginBottom: "10px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t(f.label)}</label>
                        <input className="form-control" value={settings[f.key] || ""} disabled={!isAdmin} onChange={(e) => setSettings((s) => ({ ...s, [f.key]: e.target.value }))} />
                    </div>
                ))}
                {isAdmin && <button className="btn btn-primary" style={{ marginTop: "6px" }} onClick={saveSettings}>{t("settings.save")}</button>}
            </div>

            <h3 style={{ fontSize: "15px", margin: "6px 0 12px" }}>{t("settings.dictionaries")}</h3>
            {types.map((type) => (
                <div key={type} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", marginBottom: "14px", maxWidth: "720px" }}>
                    <h4 style={{ fontSize: "14px", marginBottom: "10px" }}><code>{type}</code></h4>
                    <table className="table" style={{ width: "100%" }}>
                        <thead><tr><th>{t("settings.value")}</th><th>{t("settings.label")}</th><th>{t("settings.order")}</th><th>{t("settings.enabled")}</th>{isAdmin && <th></th>}</tr></thead>
                        <tbody>
                            {dicts[type].map((it) => (
                                <tr key={it.id}>
                                    <td><code style={{ fontSize: "12px" }}>{it.value}</code></td>
                                    <td><input className="form-control" style={{ minWidth: "120px" }} value={it.label || ""} disabled={!isAdmin} onChange={(e) => setDictField(type, it.id, { label: e.target.value })} /></td>
                                    <td><input type="number" className="form-control" style={{ width: "70px" }} value={it.sort_order} disabled={!isAdmin} onChange={(e) => setDictField(type, it.id, { sort_order: e.target.value })} /></td>
                                    <td><input type="checkbox" checked={!!it.enabled} disabled={!isAdmin} onChange={(e) => setDictField(type, it.id, { enabled: e.target.checked ? 1 : 0 })} /></td>
                                    {isAdmin && <td style={{ display: "flex", gap: "6px" }}><button className="btn btn-sm btn-primary" onClick={() => saveDict(it)}>{t("settings.update")}</button><button className="btn btn-sm btn-outline-danger" onClick={() => deleteDict(it.id)}>{t("settings.delete")}</button></td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}

            {isAdmin && (
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", maxWidth: "720px" }}>
                    <h4 style={{ fontSize: "14px", marginBottom: "12px" }}>{t("settings.add")}</h4>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div><label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("settings.type")}</label><input className="form-control" list="dict-types" style={{ width: "160px" }} placeholder={t("settings.newType")} value={newItem.type} onChange={(e) => setNewItem((p) => ({ ...p, type: e.target.value }))} /><datalist id="dict-types">{types.map((ty) => <option key={ty} value={ty} />)}</datalist></div>
                        <div><label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("settings.value")}</label><input className="form-control" style={{ width: "140px" }} value={newItem.value} onChange={(e) => setNewItem((p) => ({ ...p, value: e.target.value }))} /></div>
                        <div><label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("settings.label")}</label><input className="form-control" style={{ width: "160px" }} value={newItem.label} onChange={(e) => setNewItem((p) => ({ ...p, label: e.target.value }))} /></div>
                        <button className="btn btn-primary" onClick={addItem}>{t("settings.add")}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
